import * as React from 'react';

import { faClock } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useIntl } from 'react-intl';

import { Button } from 'src/registry/ui/Button';
import { Calendar } from 'src/registry/ui/Calendar';
import { Input } from 'src/registry/ui/Input';
import { Popover, PopoverContent, PopoverTrigger } from 'src/registry/ui/Popover';
import { generalValidationMessages } from 'src/registry/utils/messages';
import { storage } from 'src/registry/utils/storage';
import { Matcher } from 'react-day-picker';

type CalendarWithTimeProps = {
    dateISO?: string | null;
    onChange: (date: string) => void;
    placeholder?: string;
    disableTime?: boolean;
    disabledDate?: Matcher | Matcher[];
};

const TIME_FORMAT: Intl.DateTimeFormatOptions = {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
};

export default function CalendarWithTime({ dateISO, onChange, placeholder, disableTime = false, disabledDate }: CalendarWithTimeProps) {
    const { formatMessage } = useIntl();
    const date = React.useMemo(() => (dateISO ? new Date(dateISO) : new Date()), [dateISO]);

    const onDateChange = React.useCallback(
        (newDateString?: Date) => {
            const newDate = newDateString ?? new Date();
            const formattedNewDate = new Date(
                newDate.getFullYear(),
                newDate.getMonth(),
                newDate.getDate(),
                date.getHours(),
                date.getMinutes(),
            );
            onChange(formattedNewDate.toISOString());
        },
        [date, onChange],
    );

    const onTimeChange = React.useCallback(
        ({ target: { value } }: React.ChangeEvent<HTMLInputElement>) => {
            const [hours, minutes] = value.split(':').map(Number);
            const formattedNewDate = new Date(date.getFullYear(), date.getMonth(), date.getDate(), hours, minutes);
            onChange(formattedNewDate.toISOString());
        },
        [date, onChange],
    );

    const placeholderToDisplay = placeholder
        ? placeholder
        : `${formatMessage(generalValidationMessages.fieldRequiredDate)}...`;

    const locale = storage.getLanguage();
    const formattedDate = dateISO ? date.toLocaleDateString(locale) : placeholderToDisplay;
    const formattedTime = dateISO ? date.toLocaleTimeString(locale, { ...TIME_FORMAT }) : '';

    return (
        <Popover>
            <PopoverTrigger asChild>
                <Button
                    variant="outline"
                    className="flex flex-1 justify-start gap-2 p-2 text-base font-normal text-gray-500"
                >
                    {!disableTime && <span>{formattedTime}</span>}
                    <span>{formattedDate}</span>
                </Button>
            </PopoverTrigger>
            <PopoverContent
                className="flex w-auto w-fit flex-col gap-6 rounded-xl border p-0 py-4 shadow-sm"
                align="start"
                side="bottom"
            >
                <Calendar mode="single" selected={date} onSelect={onDateChange} className="bg-transparent px-4" disabled={disabledDate}/>
                {!disableTime && (
                    <div className="relative flex w-full items-center gap-2 px-4">
                        <FontAwesomeIcon
                            icon={faClock}
                            className="text-muted-foreground pointer-events-none absolute left-2.5 size-4 px-4 select-none"
                        />
                        <Input
                            onChange={onTimeChange}
                            id="time"
                            type="time"
                            step="60"
                            defaultValue={formattedTime}
                            className="appearance-none pl-8 [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-calendar-picker-indicator]:appearance-none"
                        />
                    </div>
                )}
            </PopoverContent>
        </Popover>
    );
}
